import { PopoverContentProps } from '@radix-ui/react-popover'
import { ZIndex } from '@opallabs/gem'
import { Button, ButtonSize, Popover } from '@opallabs/lapidary'
import * as styles from './styles'

type ControlButtonPopoverProps = Pick<
  PopoverContentProps,
  'align' | 'side' | 'sideOffset' | 'alignOffset'
> & {
  label: string
  icon: React.ReactElement
  children: React.ReactNode
  displayMenuIndicator?: boolean
  fixedPopoverWidth?: boolean
  focusActiveOption?: boolean
}

// Instead of the default focus behavior of the Popover,
// which focuses the first element, we want to instead
// focus the active option when there is one.
const focusActiveOptionButton = (e: Event) => {
  const popoverContent = e.currentTarget as HTMLElement
  const activeOptionButton = popoverContent?.querySelector<HTMLButtonElement>(
    '[aria-pressed="true"]'
  )

  if (activeOptionButton) {
    e.preventDefault()
    activeOptionButton.focus()
  }
}

export const ControlButtonPopover = ({
  label,
  icon,
  children,
  sideOffset,
  alignOffset,
  align = 'end',
  side = 'bottom',
  displayMenuIndicator = true,
  fixedPopoverWidth = false,
  focusActiveOption = true,
}: ControlButtonPopoverProps) => (
  <Popover.Root>
    <Popover.Trigger
      tooltip={{
        content: label,
        legacyTheme: 'dark', // Required to match designs
        sideOffset: 4,
        // Using Shroud `ZIndex` throughout since that is the highest the editor will apppear in
        zIndex: ZIndex.Shroud.Tooltip,
      }}
    >
      <Button
        aria-label={label}
        css={
          displayMenuIndicator
            ? styles.controlButtonWithPopoverButton
            : undefined
        }
        displayMenuIndicator={displayMenuIndicator}
        icon={icon}
        name={label}
        size={ButtonSize.Small}
        variant="ghost"
      />
    </Popover.Trigger>
    <Popover.Content
      align={align}
      alignOffset={alignOffset}
      onEscapeKeyDown={(e) => e.stopPropagation()}
      onOpenAutoFocus={focusActiveOption ? focusActiveOptionButton : undefined}
      side={side}
      sideOffset={sideOffset}
      zIndex={ZIndex.Shroud.Popover}
    >
      <div css={styles.controlButtonWithPopoverContent(fixedPopoverWidth)}>
        {children}
      </div>
    </Popover.Content>
  </Popover.Root>
)
